import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar, Eye, ArrowUpRight } from 'lucide-react';
import PrivateBookingBadge from '../../shared/PrivateBookingBadge';
import { isPrivateBooking } from '../../../utils/bookingSource';

const RecentBookings = ({ bookings = [] }) => {
  const navigate = useNavigate();

  // Helper function to format currency
  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined || amount === '') return '$0.00';
    const numAmount = typeof amount === 'string' ? parseFloat(amount) : Number(amount);
    if (isNaN(numAmount) || !isFinite(numAmount)) return '$0.00';
    return `$${numAmount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  // Helper function to format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  // Get status color
  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'confirmed':
        return 'bg-blue-100 text-blue-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="lg:col-span-2 bg-white rounded-lg border border-gray-200 p-4 sm:p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-gray-900">Recent Bookings</h2>
        <button
          onClick={() => navigate('/admin/bookings')}
          className="text-sm text-primary-600 hover:text-primary-700 font-medium flex items-center gap-1"
        >
          View All
          <ArrowUpRight className="w-4 h-4" />
        </button>
      </div>
      {bookings.length > 0 ? (
        <div className="space-y-3">
          {bookings.map((booking) => (
            <div
              key={booking.id}
              onClick={() => navigate(`/admin/bookings/${booking.id}`)}
              className="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <p className="font-medium text-gray-900 truncate">
                    {booking.booking_reference || `#${booking.id.substring(0, 8)}`}
                  </p>
                  {isPrivateBooking(booking) && <PrivateBookingBadge />}
                  <span className={`px-2 py-1 rounded text-xs font-medium ${getStatusColor(booking.status)}`}>
                    {booking.status}
                  </span>
                </div>
                <div className="flex items-center gap-4 text-sm text-gray-600">
                  {booking.client && (
                    <span className="truncate">{booking.client.name}</span>
                  )}
                  {booking.event_date && (
                    <span className="flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      {formatDate(booking.event_date)}
                    </span>
                  )}
                  {booking.subtotal_amount && (
                    <span className="font-medium text-gray-900">
                      {formatCurrency(booking.subtotal_amount)}
                    </span>
                  )}
                </div>
              </div>
              <Eye className="w-5 h-5 text-gray-400 flex-shrink-0" />
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-8">
          <Calendar className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600">No recent bookings</p>
        </div>
      )}
    </div>
  );
};

export default RecentBookings;

